import { action, computed, observable, makeObservable } from 'mobx';
import {arraySwapItems, Control, StringControl} from '../../../../../lib/helpers';
import {GapFillingBehavior, GapFillingModel, GapItem} from '../models';
import {GapFillingCrudHelper} from './gap-filling-crud-helper';
import {ErrorItem, GapFillingCrudModel, GapFillingCrudParagraph} from './models';

export class GapStore {
  value = new StringControl('', '');
  placeholder = new StringControl('', '');

  constructor() {
    makeObservable(this);
  }

  @computed
  get valid(): boolean {
    return this.value.value.trim() !== '';
  }

  @computed
  get model(): GapItem | ErrorItem {
    if (!this.valid)
      return {type: 'gap-error', message: 'no value specified'};
    return {type: 'gap', value: this.value.value, placeholder: this.placeholder.value};
  }

  @action
  sync = (model: GapItem) => {
    this.value.setValue(model.value);
    this.placeholder.setValue(model.placeholder);
  };
}

export class GapFillingEditStore {
  readonly behavior: Control<GapFillingBehavior>;

  readonly serialized: StringControl;

  @observable.shallow
  readonly gaps: GapStore[];

  constructor() {
    makeObservable(this);
    this.behavior = new Control<GapFillingBehavior>('', 'drag-and-drop');
    this.serialized = new StringControl('', '');
    this.gaps = [];
  }

  @action
  setModel = (model: GapFillingModel) => {
    this.behavior.setValue(model.behavior);
    this.serialized.setValue(GapFillingCrudHelper.serializeParagraphs(model.paragraphs));
    this.gaps.splice(0);
    for (let gapItem of GapFillingCrudHelper.gapItemsList(model.paragraphs)) {
      const gap = new GapStore();
      gap.sync(gapItem);
      this.gaps.push(gap);
    }
  };

  @computed
  get paragraphs(): GapFillingCrudParagraph[] {
    return GapFillingCrudHelper.deserializeParagraphs(
      this.serialized.value,
      this.gaps.map(x => x.model));
  }

  @computed
  get valid(): boolean {
    const gapCount = this.gapCountFromSerialized;
    if (gapCount > this.gaps.length)
      return false;
    for (let i = 0; i < gapCount; i++) {
      let gap = this.gaps[i];
      if (!gap.valid)
        return false;
    }
    return true;
  }

  @computed
  get model(): GapFillingCrudModel {
    return {
      behavior: this.behavior.value,
      paragraphs: this.paragraphs
    };
  }

  @computed
  get x9Content(): string {
    if (!this.valid)
      return '';
    return JSON.stringify(this.model);
  }

  @computed
  get gapCountFromSerialized() {
    return GapFillingCrudHelper.countGaps(this.serialized.value);
  }

  @computed
  get hasExtraGapStores() {
    return this.gaps.length > this.gapCountFromSerialized;
  }

  @computed
  get hasMissingGapStores() {
    return this.gaps.length < this.gapCountFromSerialized;
  }

  @action
  swapUp(index: number) {
    arraySwapItems(this.gaps, index, index - 1);
  }

  @action
  swapDown(index: number) {
    arraySwapItems(this.gaps, index, index + 1);
  }

  @action
  addGapStore(index: number) {
    this.gaps.splice(index, 0, new GapStore());
  }

  @action
  removeGapStore(index: number) {
    this.gaps.splice(index, 1);
  }
}
